import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

// Safe structured body editor: paragraphs, H2/H3, lists, quotes — plain
// text blocks only, no arbitrary HTML or JavaScript. The public article
// template renders these blocks.
export function BlocksEditor({ value, onChange, id }) {
  const blocks = Array.isArray(value) ? value : [];
  const update = (i, patch) => onChange(blocks.map((b, j) => (j === i ? { ...b, ...patch } : b)));
  const move = (i, dir) => {
    const next = [...blocks];
    const [item] = next.splice(i, 1);
    next.splice(i + dir, 0, item);
    onChange(next);
  };
  const remove = (i) => onChange(blocks.filter((_, j) => j !== i));
  const add = (type) => onChange([...blocks, type === 'ul' ? { type, items: [''] } : { type, text: '' }]);

  return (
    <div className="mt-1 space-y-2" data-testid={`${id}-blocks`}>
      {blocks.length === 0 && (
        <p className="rounded-md border border-dashed border-slate-300 p-3 text-xs text-slate-500">
          No content blocks yet. Add paragraphs and headings below.
        </p>
      )}
      {blocks.map((block, i) => (
        <div key={i} className="flex items-start gap-2 rounded-md border border-slate-200 bg-white p-2">
          <select
            aria-label={`Block ${i + 1} type`}
            value={block.type}
            onChange={(e) => update(i, { type: e.target.value })}
            className="h-9 w-24 shrink-0 rounded-md border border-slate-300 text-xs font-bold"
          >
            <option value="p">Paragraph</option>
            <option value="h2">Heading 2</option>
            <option value="h3">Heading 3</option>
            <option value="quote">Quote</option>
            <option value="ul">List</option>
          </select>
          {block.type === 'ul' ? (
            <textarea
              aria-label={`Block ${i + 1} list items (one per line)`}
              rows={3}
              value={(block.items || []).join('\n')}
              onChange={(e) => update(i, { items: e.target.value.split('\n') })}
              placeholder="One list item per line"
              className="min-h-[4rem] flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          ) : (
            <textarea
              aria-label={`Block ${i + 1} text`}
              rows={block.type === 'p' ? 3 : 1}
              value={block.text || ''}
              onChange={(e) => update(i, { text: e.target.value })}
              className="flex-1 rounded-md border border-slate-200 px-2 py-1.5 text-sm"
            />
          )}
          <div className="flex shrink-0 flex-col gap-1">
            <button type="button" aria-label="Move block up" disabled={i === 0} onClick={() => move(i, -1)} className="rounded p-1 text-slate-400 hover:text-navy-900 disabled:opacity-30">
              <ArrowUp className="h-3.5 w-3.5" />
            </button>
            <button type="button" aria-label="Move block down" disabled={i === blocks.length - 1} onClick={() => move(i, 1)} className="rounded p-1 text-slate-400 hover:text-navy-900 disabled:opacity-30">
              <ArrowDown className="h-3.5 w-3.5" />
            </button>
            <button type="button" aria-label="Remove block" onClick={() => remove(i)} className="rounded p-1 text-slate-400 hover:text-red-600">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        {[['p', 'Paragraph'], ['h2', 'Heading 2'], ['h3', 'Heading 3'], ['quote', 'Quote'], ['ul', 'List']].map(([t, label]) => (
          <button key={t} type="button" onClick={() => add(t)} data-testid={`${id}-add-${t}`} className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-xs font-bold text-navy-900 hover:bg-navy-50">
            <Plus className="h-3 w-3" /> {label}
          </button>
        ))}
      </div>
      <p className="mt-2 text-xs font-medium text-slate-400">Inline formatting in text: **bold**, *italic*, [link text](https://…)</p>
    </div>
  );
}
